'use client';

import { motion, useReducedMotion, type HTMLMotionProps } from 'framer-motion';
import { useEffect, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';

type SectionTag = 'section' | 'article' | 'div' | 'p';

const motionByTag = {
  section: motion.section,
  article: motion.article,
  div: motion.div,
  p: motion.p,
} as const;

/** Scroll-reveal delay in seconds. Use `staggerDelay(index)` for grid children. */
export function staggerDelay(index: number, step = 0.08) {
  return index * step;
}

export type FadeUpSectionProps = {
  as?: SectionTag;
  delay?: number;
  className?: string;
  children: ReactNode;
} & Omit<HTMLMotionProps<'section'>, 'children'>;

export function FadeUpSection({
  as = 'section',
  delay = 0,
  className,
  children,
  style,
  ...props
}: FadeUpSectionProps) {
  const ref = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();
  const [revealed, setRevealed] = useState(true);

  useEffect(() => {
    if (reduceMotion) return;

    const el = ref.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    // If already visible on mount, keep revealed = true (no animation needed)
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      return;
    }

    // Only animate sections that start below the fold
    setRevealed(false);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px 120px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [reduceMotion]);

  const Tag = as;
  const MotionTag = motionByTag[as];

  if (reduceMotion) {
    return (
      <Tag ref={ref as never} className={className} style={style} {...(props as HTMLAttributes<HTMLElement>)}>
        {children}
      </Tag>
    );
  }

  return (
    <MotionTag
      ref={ref}
      className={className}
      style={style}
      animate={revealed ? { opacity: 1, y: 0 } : { opacity: 0, y: 32 }}
      transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
        delay: revealed ? delay : 0,
      }}
      {...props}
    >
      {children}
    </MotionTag>
  );
}
